import { WarrantyComparator } from '@policyvision/contracts';
import { useTranslation } from 'react-i18next';

import { TableCell, TableRow } from '@/common/components/ui/table';
import { VariationBadge } from '@/common/components/ui/variation-badge';
import { useFormatCurrency } from '@/common/hooks/useFormatCurrency';

interface ComparatorFooterProps {
  warranties: WarrantyComparator[];
}

export const ComparatorFooter = ({ warranties }: ComparatorFooterProps) => {
  const { t } = useTranslation();
  const { formatCurrency } = useFormatCurrency();

  const lastYearPremiums = warranties.reduce(
    (total, warranty) =>
      total +
      (Number.parseFloat(warranty.lastYear.premium.replace(/[^\d.-]/g, '')) ||
        0),
    0,
  );
  const currentYearPremiums = warranties.reduce(
    (total, warranty) =>
      total +
      (Number.parseFloat(
        warranty.currentYear.premium.replace(/[^\d.-]/g, ''),
      ) || 0),
    0,
  );

  const amountVariations = warranties.filter(
    (warranty) => warranty.variation.amount !== undefined,
  );
  const rateVariations = warranties.filter(
    (warranty) => warranty.variation.rate !== undefined,
  );

  const avgAmountVariation =
    amountVariations.reduce(
      (total, warranty) => total + (warranty.variation.amount ?? 0),
      0,
    ) / amountVariations.length;
  const avgRateVariation =
    rateVariations.reduce(
      (total, warranty) => total + (warranty.variation.rate ?? 0),
      0,
    ) / rateVariations.length;

  return (
    <TableRow key="footer">
      <TableCell className="bg-terre-600 font-semibold text-xs text-white rounded-bl-lg">
        {t('renewal.comparator.grandTotal')}
      </TableCell>
      <TableCell
        colSpan={3}
        className="bg-terre-600 font-normal text-xs text-white text-right border-r-2 border-warm-gray-200"
      >
        {formatCurrency(lastYearPremiums.toPrecision(1))}
      </TableCell>
      <TableCell
        colSpan={3}
        className="bg-terre-600 font-normal text-xs text-white text-right border-r-2 border-warm-gray-200"
      >
        {formatCurrency(currentYearPremiums.toPrecision(1))}
      </TableCell>
      <TableCell className="bg-terre-600 border-r border-warm-gray-200 text-center">
        {Number.isNaN(avgAmountVariation) || (
          <VariationBadge value={avgAmountVariation} />
        )}
      </TableCell>
      <TableCell className="bg-terre-600 rounded-br-lg text-center">
        {Number.isNaN(avgRateVariation) || (
          <VariationBadge value={avgRateVariation} />
        )}
      </TableCell>
    </TableRow>
  );
};
